'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  Paperclip,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  File
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatFileSize } from '../utils/date-utils';
import { toast } from '@/lib/toast';
import type { Attachment } from '../types';
// Import file utilities for proper preview/download handling
import { canPreviewFile } from '@/lib/services/files/file-utils';
import { useAttachmentPreview } from '../contexts/attachment-preview-context';

interface AttachmentDropdownProps {
  attachment: Attachment;
  className?: string;
  isUserMessage?: boolean;
}

// Get file icon based on mime type
function getFileIcon(mimeType?: string) {
  if (!mimeType) return <File className="h-4 w-4" />;

  if (mimeType.startsWith('image/')) return <ImageIcon className="h-4 w-4" />;
  if (mimeType.includes('pdf')) return <FileText className="h-4 w-4" />;
  if (mimeType.includes('sheet') || mimeType.includes('excel'))
    return <FileSpreadsheet className="h-4 w-4" />;
  if (mimeType.includes('document') || mimeType.includes('word'))
    return <FileText className="h-4 w-4" />;

  return <File className="h-4 w-4" />;
}

export function AttachmentDropdown({
  attachment,
  className,
  isUserMessage = false
}: AttachmentDropdownProps) {
  const previewCtx = useAttachmentPreview();

  // Get the filename - support both API formats
  const filename =
    attachment.filename ||
    (attachment as { originalFilename?: string }).originalFilename ||
    'Attachment';

  // Handle download
  const handleDownload = () => {
    try {
      // Ensure we have a valid file ID for the download endpoint
      const fileId = attachment.id;
      if (!fileId || (typeof fileId === 'string' && isNaN(parseInt(fileId)))) {
        console.error('Invalid file ID for download:', fileId);
        toast.error('Unable to download file - invalid file ID');
        return;
      }

      // Use the correct proxy endpoint for file download
      // The backend will stream the file from S3 with attachment disposition
      const downloadUrl = `/api/proxy/files/${fileId}/download?preview=false`;

      // Open in new tab to trigger download
      // The Content-Disposition: attachment header will force download
      window.open(downloadUrl, '_blank');
      // No success toast needed - the browser will show download progress
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    }
  };

  // Handle preview
  const handlePreview = () => {
    // If a parent has provided an attachment preview context, use it
    if (previewCtx) {
      previewCtx.onPreview(attachment);
      return;
    }

    try {
      const fileId = attachment.id;
      if (!fileId || (typeof fileId === 'string' && isNaN(parseInt(fileId)))) {
        console.error('Invalid file ID for preview:', fileId);
        toast.error('Unable to preview file - invalid file ID');
        return;
      }

      const previewUrl = `/api/proxy/files/${fileId}/download?preview=true`;
      window.open(previewUrl, '_blank');
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Failed to preview file');
    }
  };

  // Check if file can be previewed using the utility function
  const canPreview = canPreviewFile(attachment.mimeType);

  // When a preview context is available, click directly opens the tab
  if (previewCtx && canPreview) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'h-auto w-full justify-start p-1 text-left font-normal',
          isUserMessage
            ? 'hover:bg-primary-foreground/10'
            : 'hover:bg-background/50',
          className
        )}
        onClick={handlePreview}
      >
        <div className="flex w-full items-center gap-2 text-xs">
          <Paperclip className="h-3 w-3 shrink-0" />
          <span className="flex-1 truncate">{filename}</span>
          {attachment.size && (
            <span className="text-xs opacity-70">
              {formatFileSize(attachment.size)}
            </span>
          )}
        </div>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-auto w-full justify-start p-1 text-left font-normal',
            isUserMessage
              ? 'hover:bg-primary-foreground/10'
              : 'hover:bg-background/50',
            className
          )}
        >
          <div className="flex w-full items-center gap-2 text-xs">
            <Paperclip className="h-3 w-3 shrink-0" />
            <span className="flex-1 truncate">{filename}</span>
            {attachment.size && (
              <span className="text-xs opacity-70">
                {formatFileSize(attachment.size)}
              </span>
            )}
          </div>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-56">
        <div className="px-2 py-1.5">
          <div className="mb-1 flex items-center gap-2">
            {getFileIcon(attachment.mimeType)}
            <p className="truncate text-sm font-medium">{filename}</p>
          </div>
          {attachment.size && (
            <p className="text-muted-foreground text-xs">
              Size: {formatFileSize(attachment.size)}
            </p>
          )}
          {attachment.mimeType && (
            <p className="text-muted-foreground text-xs capitalize">
              Type: {attachment.mimeType.split('/').pop()}
            </p>
          )}
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleDownload}>
          <Download className="mr-2 h-4 w-4" />
          Download
        </DropdownMenuItem>

        {canPreview && (
          <DropdownMenuItem onClick={handlePreview}>
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
